<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { CRUISE, HOTEL_ONLY, PACKAGE, TOUR } from '@/constants';

interface Props {
  infoData: any;
  priceData: any;
  quoteType: any;
  option: any;
  hotelTitle?: string;
}
const props = defineProps<Props>();

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();

const getRoomOption = (rooms: any) => {
  return rooms.length ? rooms.map((room: any) => `${getTextVariant(currentLanguage, room.roomName, room.roomNameFr)} (${room.roomCode})`).join(', \n') : '';
};

const renderLeadText = () => {
  switch (props.quoteType) {
    case TOUR:
      return `${t('common.tour')}:`;
    case PACKAGE:
      return `${t('common.package')}:`;
    case HOTEL_ONLY:
      return `${t('common.hotel')}:`;
    case CRUISE:
      return props.hotelTitle;
    default:
      return '';
  }
};

const renderDataText = () => {
  switch (props.quoteType) {
    case TOUR:
      return `${t('common.option')} ${props.option + 1} - ${getTextVariant(currentLanguage, props.infoData?.tourNameEn, props.infoData?.tourNameFr)} - ${formatDate(props.infoData.depDate, 'dd-LLL-yyyy', false, currentLanguage)}`;
    case HOTEL_ONLY:
      return `${t('common.option')} ${props.option + 1}`;
    default:
      return `${t('common.option')} ${props.option + 1}`;
  }
};
</script>
<template>
  <div>
    <div>
      <div class="lead-option">{{ renderLeadText() }} {{ renderDataText() }}</div>
      <div v class="detail-option flex justify-content-between flex-wrap lg:flex-nowrap">
        <div>
          <div class="destination font-bold mt-2 mb-2">
            <div v-if="quoteType !== HOTEL_ONLY && quoteType !== CRUISE" class="flex gap-3">
              <div class="min-w-label">{{ $t('common.gateway') }}:</div>
              <div class="font-normal">{{ infoData.gateway }} ({{ infoData.gateCode }})</div>
            </div>
          </div>
          <div class="destination font-bold mb-2">
            <div class="flex gap-3">
              <div class="min-w-label">{{ $t('common.destination') }}:</div>
              <div class="font-normal">{{ getTextVariant(currentLanguage, infoData?.destination, props.infoData?.destinationFr) }} ({{ infoData.destCode }})</div>
            </div>
          </div>
          <div class="hotel font-bold mb-2">
            <div>
              <div v-if="quoteType === TOUR" class="flex gap-3">
                <div class="min-w-label">{{ $t('common.tour') }}:</div>
                <div class="font-normal white-space-pre-line">{{ getTextVariant(currentLanguage, infoData?.tourNameEn, infoData?.tourNameFr) }} ({{ infoData.ttsId.substring(0, 6) }})</div>
              </div>
              <div v-else class="flex gap-3">
                <div class="min-w-label">{{ $t('common.hotel') }}:</div>
                <div class="font-normal white-space-pre-line">{{ `${infoData.hotel} (${infoData?.hCode})` }}</div>
              </div>
            </div>
          </div>
          <div class="hotel-room-cat font-bold mb-2">
            <div class="flex gap-3">
              <div v-if="quoteType === TOUR" class="min-w-label">{{ $t('management.tour.list_tour_detail.tour_type') }}:</div>
              <div v-else class="min-w-label">{{ $t('worksheet.hotel_room_category') }}:</div>
              <div class="font-normal white-space-pre-line">{{ getRoomOption(priceData) }}</div>
            </div>
          </div>
          <div class="departure-date font-bold mb-2">
            <div class="flex gap-3">
              <div class="min-w-label">{{ $t('worksheet.departure_date') }}:</div>
              <div class="font-normal">{{ formatDate(infoData.depDate, 'dd-LLL-yyyy', false, currentLanguage) }}</div>
            </div>
          </div>
          <div class="duration font-bold mb-2">
            <div v-if="quoteType !== TOUR" class="flex gap-3">
              <div class="min-w-label">{{ $t('worksheet.duration') }}:</div>
              <div class="font-normal">{{ infoData.duration }}</div>
            </div>
          </div>
        </div>
        <div class="flex flex-column align-items-end">
          <div class="adults font-bold mb-2 mt-2">
            <div class="flex">
              <div class="min-w-label">{{ $t('common.adults') }}:</div>
              <div class="font-normal w-6rem">{{ infoData.adults ?? 0 }}</div>
            </div>
          </div>
          <div class="child font-bold mb-2">
            <div class="flex">
              <div class="min-w-label">{{ $t('common.children') }}:</div>
              <div class="font-normal w-6rem">{{ infoData.children ?? 0 }}</div>
            </div>
          </div>
          <div class="infant font-bold mb-2">
            <div class="flex">
              <div class="min-w-label">{{ $t('common.infant') }}:</div>
              <div class="font-normal w-6rem">{{ infoData.infants ?? 0 }}</div>
            </div>
          </div>
          <div class="number_room font-bold mb-2">
            <div class="flex">
              <div class="min-w-label">{{ $t('advance_quote.form.number_of_room') }}:</div>
              <div class="font-normal w-6rem">{{ infoData.numOfRooms ?? 0 }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
